import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import Navbar from "../../../../posnavbar/Navbar";
import { useNavigate } from "react-router-dom";
import { UserContext } from "App";
import useAuthModal from "../../../hooks/useAuthModal";
import Swal from "sweetalert2";
import "./index.css";

/* ─────────────────────────────────────────────
   Static content
───────────────────────────────────────────── */
const TIPS = [
  "Clear browser cache if images fail to load",
  "Check thermal printer paper roll alignment",
  "Verify EDC terminal is on the same WiFi network",
  "Refresh the page if the cart feels unresponsive",
  "Restart the app after a session close if performance degrades",
];

/* Diagnostic steps shown during "Run Diagnostics" */
const DIAG_STEPS = [
  { key: "latency",  label: "Checking API latency…"      },
  { key: "fps",      label: "Measuring UI frame rate…"   },
  { key: "network",  label: "Reading network speed…"     },
  { key: "memory",   label: "Analysing memory usage…"    },
  { key: "pageload", label: "Evaluating page load time…" },
];

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
/** Map a value onto a 0-100 fill %, clamped */
const toBar = (val, good, max) => val == null ? 0 : Math.min(100, Math.round((val / max) * 100));

/** Colour token from status */
const statusColor = (s) =>
  ({ good: "#22c55e", warn: "#f59e0b", bad: "#ef4444", idle: "#cbd5e1" }[s] ?? "#cbd5e1");

/** Score contribution per metric — each worth 25pts */
const scoreOf = (status) => ({ good: 25, warn: 12, bad: 3, idle: 0 }[status] ?? 0);

/* SVG score ring */
const ScoreRing = ({ score, color }) => {
  const r = 44, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" className="qts-score-ring">
      <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" className="qts-score-ring-bg" strokeWidth="10" />
      <circle
        cx="60" cy="60" r={r} fill="none"
        stroke={color} strokeWidth="10"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 60 60)"
        style={{ transition: "stroke-dasharray 0.8s cubic-bezier(.4,0,.2,1)" }}
      />
      <text x="60" y="64" textAnchor="middle" className="qts-score-text"
        style={{ fill: color, fontSize: 26, fontWeight: 800, fontFamily: "inherit" }}>
        {score}
      </text>
    </svg>
  );
};

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const QuickTroubleshoot = () => {
  const navigate = useNavigate();
  const { color } = useContext(UserContext);
  const { renderAuthModal, openAuthModal } = useAuthModal();
  const isDark = color === "dark";

  /* ── metric state ── */
  const [apiLatency, setApiLatency] = useState(null); // ms
  const [pageLoad,   setPageLoad]   = useState(null); // s
  const [netSpeed,   setNetSpeed]   = useState(null); // Mbps
  const [fps,        setFps]        = useState(null);
  const [memPct,     setMemPct]     = useState(null); // 0-100
  const [netType,    setNetType]    = useState(null);
  const [isOnline,   setIsOnline]   = useState(navigator.onLine);

  /* ── diagnostics animation state ── */
  const [diagRunning,   setDiagRunning]   = useState(false);
  const [diagStep,      setDiagStep]      = useState(-1);    // index into DIAG_STEPS (-1 = idle)
  const [diagDone,      setDiagDone]      = useState(false);
  const [lastMeasured,  setLastMeasured]  = useState(null);  // Date

  /* ── individual measurement functions ── */
  const measureLatency = useCallback(async () => {
    try {
      const t0 = performance.now();
      await fetch(window.location.origin, { method: "HEAD", cache: "no-store" });
      setApiLatency(Math.round(performance.now() - t0));
    } catch { setApiLatency(null); }
  }, []);

  const measureFps = useCallback(() => new Promise((resolve) => {
    let frames = 0; const start = performance.now();
    const tick = () => {
      frames++;
      if (performance.now() - start >= 1000) { setFps(frames); resolve(); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }), []);

  const readNetwork = useCallback(async () => {
    // Always read connection type from the Network Information API
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn?.effectiveType) setNetType(conn.effectiveType);

    // Measure real download speed by timing a fetch of a known-size payload
    try {
      const t0 = performance.now();
      const res = await fetch(`/api/speed-test?_=${Date.now()}`, { cache: "no-store" });
      const blob = await res.blob();
      const elapsed = (performance.now() - t0) / 1000; // seconds
      if (elapsed > 0 && blob.size > 0) {
        const mbps = parseFloat(((blob.size * 8) / elapsed / 1e6).toFixed(1));
        setNetSpeed(mbps);
        return;
      }
    } catch { /* fall through */ }

    // Fallback: use the browser's estimated downlink value
    if (conn?.downlink != null) setNetSpeed(conn.downlink);
  }, []);

  const readMemory = useCallback(() => {
    if (performance?.memory) {
      const pct = Math.round((performance?.memory?.usedJSHeapSize / performance?.memory?.jsHeapSizeLimit) * 100);
      setMemPct(pct);
    }
  }, []);

  const readPageLoad = useCallback(() => {
    try {
      const [nav] = performance.getEntriesByType("navigation");
      if (nav) { setPageLoad(+(nav.domContentLoadedEventEnd / 1000).toFixed(2)); return; }
      const fcp = performance.getEntriesByName("first-contentful-paint")[0];
      if (fcp) setPageLoad(+(fcp.startTime / 1000).toFixed(2));
    } catch { /* not available */ }
  }, []);

  /* ── step-by-step diagnostics runner ── */
  const runDiagnostics = useCallback(async () => {
    if (diagRunning) return;
    setDiagRunning(true);
    setDiagDone(false);

    const stepDelay = (ms) => new Promise((r) => setTimeout(r, ms));

    // Step 0 – latency
    setDiagStep(0);
    await measureLatency();
    await stepDelay(400);

    // Step 1 – FPS
    setDiagStep(1);
    await measureFps();
    await stepDelay(200);

    // Step 2 – network (real download test — await it)
    setDiagStep(2);
    setIsOnline(navigator.onLine);
    await readNetwork();
    await stepDelay(200);

    // Step 3 – memory
    setDiagStep(3);
    readMemory();
    await stepDelay(400);

    // Step 4 – page load
    setDiagStep(4);
    readPageLoad();
    await stepDelay(300);

    setLastMeasured(new Date());
    setDiagStep(-1);
    setDiagDone(true);
    setDiagRunning(false);
  }, [diagRunning, measureLatency, measureFps, readNetwork, readMemory, readPageLoad]);

  /* Run once on mount */
  useEffect(() => {
    runDiagnostics();
    const handle = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", handle);
    window.addEventListener("offline", handle);
    return () => { window.removeEventListener("online", handle); window.removeEventListener("offline", handle); };
  }, []);

  /* ── derived statuses ── */
  const latStatus  = apiLatency == null ? "idle" : apiLatency < 100 ? "good" : apiLatency < 300 ? "warn" : "bad";
  const fpsStatus  = fps        == null ? "idle" : fps >= 50        ? "good" : fps >= 30         ? "warn" : "bad";
  const netStatus  = !isOnline           ? "bad"  : netSpeed == null ? "idle" : netSpeed >= 10   ? "good" : "warn";
  const loadStatus = pageLoad   == null  ? "idle" : pageLoad < 2    ? "good" : pageLoad < 5      ? "warn" : "bad";

  const score = scoreOf(latStatus) + scoreOf(fpsStatus) + scoreOf(netStatus) + scoreOf(loadStatus);
  const scoreLabel = score >= 80 ? "Excellent" : score >= 55 ? "Good" : score >= 30 ? "Fair" : "Poor";
  const scoreCol   = score >= 80 ? "#22c55e"   : score >= 55 ? "#3b82f6" : score >= 30 ? "#f59e0b" : "#ef4444";

  const highMem = memPct != null && memPct > 75;
  const lowFps  = fps    != null && fps < 30;
  const highLat = apiLatency != null && apiLatency > 300;
  const stableNet = isOnline && (!netType || netType === "4g");

  const risks = [
    !isOnline && { level: "error", title: "No Internet Connection",   desc: "The device is offline." },
    highLat   && { level: "warn",  title: "High API Latency",          desc: `Server round-trip is ${apiLatency}ms. Check your network.` },
    lowFps    && { level: "warn",  title: "Low UI Responsiveness",     desc: `Only ${fps} FPS detected. Browser may be under load.` },
    highMem   && { level: "warn",  title: "High Memory Usage",         desc: `Heap is at ${memPct}%. Consider refreshing.` },
  ].filter(Boolean);

  /* ── clear local storage ── */
  const newSwal = Swal.mixin({
    customClass: { confirmButton: "button button-success", cancelButton: "button button-error", container: "close-session-swal-container" },
    buttonsStyling: false,
  });
  const handleClearStorage = () => {
    openAuthModal(() => {
      localStorage.clear();
      newSwal.fire({ title: "Cleared!", text: "Local storage has been cleared.", icon: "success", timer: 1500, showConfirmButton: false });
    }, false, { maxLength: 4 });
  };

  /* ── sub-components ── */
  const MetricCard = ({ icon, value, unit, label, sub, status }) => (
    <div className="qts-metric-card">
      <div className="qts-metric-top">
        <span className="qts-metric-icon">{icon}</span>
        <span className={`qts-metric-dot qts-metric-dot--${status}`} />
      </div>
      <div className="qts-metric-value">
        {value != null
          ? <>{value}<span className="qts-metric-unit">{unit}</span></>
          : <span className="qts-metric-dash">—</span>}
      </div>
      <div className="qts-metric-label">{label}</div>
      <div className="qts-metric-sub">{sub}</div>
    </div>
  );

  /* Snapshot metric row: name + fill bar + value */
  const SnapshotRow = ({ icon, label, value, unit, status, barPct, thresholdLabel }) => (
    <div className="qts-snap-row">
      <div className="qts-snap-row-left">
        <span className="qts-snap-icon" style={{ color: statusColor(status) }}>{icon}</span>
        <div className="qts-snap-meta">
          <span className="qts-snap-name">{label}</span>
          <span className="qts-snap-thresh">{thresholdLabel}</span>
        </div>
      </div>
      <div className="qts-snap-bar-wrap">
        <div className="qts-snap-bar-track">
          <div
            className="qts-snap-bar-fill"
            style={{ width: `${barPct}%`, background: statusColor(status) }}
          />
        </div>
      </div>
      <div className="qts-snap-value" style={{ color: statusColor(status) }}>
        {value != null ? `${value}${unit}` : "—"}
      </div>
    </div>
  );

  return (
    <Navbar>
      {renderAuthModal()}
      <div className={`qts-page${isDark ? " dark" : ""}`}>

        {/* ── Sticky Header ── */}
        <div className="qts-sticky-header">
          <div className="qts-header-inner">
            <div className="qts-header-left">
              <button className="theme-back-button" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
                </svg>
              </button>
              <h1 className="qts-header-title">System Diagnostics</h1>
            </div>
            {lastMeasured && (
              <span className="qts-last-measured">
                Last run {lastMeasured.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
          </div>
        </div>

        <div className="qts-body">

          {/* ── 4 Metric Cards ── */}
          <div className="qts-metrics-grid">
            <MetricCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></svg>}
              value={apiLatency} unit="ms" label="API Latency" sub="Time to reach server" status={latStatus}
            />
            <MetricCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>}
              value={pageLoad} unit="s" label="Page Load" sub="DOM content loaded" status={loadStatus}
            />
            <MetricCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>}
              value={netSpeed} unit=" Mbps" label="Internet Speed" sub="Downlink bandwidth" status={netStatus}
            />
            <MetricCard
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
              value={fps} unit=" FPS" label="UI Responsiveness" sub="Render frame rate" status={fpsStatus}
            />
          </div>

          {/* ── Performance Snapshot ── */}
          <div className="qts-snapshot-panel">

            {/* Left: score ring + run button */}
            <div className="qts-snapshot-score-col">
              <ScoreRing score={score} color={scoreCol} />
              <div className="qts-score-label" style={{ color: scoreCol }}>{scoreLabel}</div>
              <div className="qts-score-sub">Overall Health Score</div>

              <button
                className={`qts-diag-btn${diagRunning ? " qts-diag-btn--running" : ""}`}
                onClick={runDiagnostics}
                disabled={diagRunning}
              >
                {diagRunning ? (
                  <>
                    <span className="qts-diag-spinner" />
                    Running…
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    Run Diagnostics
                  </>
                )}
              </button>

              {/* Step progress during run */}
              {diagRunning && diagStep >= 0 && (
                <div className="qts-diag-steps">
                  {DIAG_STEPS?.map((s, i) => (
                    <div key={s?.key} className={`qts-diag-step${i < diagStep ? " done" : i === diagStep ? " active" : ""}`}>
                      <span className="qts-diag-step-dot" />
                      <span className="qts-diag-step-label">{s?.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {diagDone && !diagRunning && (
                <div className="qts-diag-done">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                    fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Diagnostics complete
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="qts-snapshot-divider" />

            {/* Right: metric bars */}
            <div className="qts-snapshot-bars-col">
              <div className="qts-snap-col-title">Metric Breakdown</div>
              <SnapshotRow
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></svg>}
                label="API Latency" value={apiLatency} unit="ms" status={latStatus}
                barPct={apiLatency == null ? 0 : Math.max(5, 100 - Math.min(100, (apiLatency / 500) * 100))}
                thresholdLabel="Good < 100ms"
              />
              <SnapshotRow
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
                label="UI Frame Rate" value={fps} unit=" FPS" status={fpsStatus}
                barPct={fps == null ? 0 : Math.min(100, Math.round((fps / 60) * 100))}
                thresholdLabel="Good ≥ 50 FPS"
              />
              <SnapshotRow
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>}
                label="Internet Speed" value={netSpeed} unit=" Mbps" status={netStatus}
                barPct={netSpeed == null ? 0 : Math.min(100, Math.round((netSpeed / 50) * 100))}
                thresholdLabel="Good ≥ 10 Mbps"
              />
              <SnapshotRow
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>}
                label="Page Load" value={pageLoad} unit="s" status={loadStatus}
                barPct={pageLoad == null ? 0 : Math.max(5, 100 - Math.min(100, (pageLoad / 8) * 100))}
                thresholdLabel="Good < 2s"
              />
              {memPct != null && (
                <SnapshotRow
                  icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M12 12h.01M8 12h.01M16 12h.01"/></svg>}
                  label="Memory Usage" value={memPct} unit="%" status={highMem ? "warn" : "good"}
                  barPct={memPct}
                  thresholdLabel="Good < 75%"
                />
              )}
            </div>
          </div>

          {/* ── Bottom row: Risks + Tips ── */}
          <div className="qts-bottom-row">

            {/* Potential Risks */}
            <div className="qts-panel">
              <div className="qts-panel-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                  fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                  <path d="M12 9v4"/><path d="M12 17h.01"/>
                </svg>
                <span className="qts-panel-title">Potential Risks</span>
                {risks?.length > 0 && <span className="qts-risk-badge">{risks?.length}</span>}
              </div>

              {risks?.length === 0 ? (
                <>
                  <div className="qts-risk-row">
                    <span className="qts-risk-dot qts-risk-dot--good" />
                    <div>
                      <div className="qts-risk-title">Memory Usage{memPct != null ? ` — ${memPct}%` : ""}</div>
                      <div className="qts-risk-desc">Heap usage is within normal range.</div>
                    </div>
                  </div>
                  <div className="qts-risk-row">
                    <span className="qts-risk-dot qts-risk-dot--good" />
                    <div>
                      <div className="qts-risk-title">Network Stability</div>
                      <div className="qts-risk-desc">{stableNet ? "Connection is stable with low jitter." : `Signal type: ${netType || "unknown"}.`}</div>
                    </div>
                  </div>
                </>
              ) : risks?.map((r, i) => (
                <div key={i} className={`qts-risk-row qts-risk-row--${r?.level}`}>
                  <span className={`qts-risk-dot qts-risk-dot--${r?.level}`} />
                  <div>
                    <div className="qts-risk-title">{r?.title}</div>
                    <div className="qts-risk-desc">{r?.desc}</div>
                  </div>
                </div>
              ))}

              <div className="qts-danger-zone">
                <button className="qts-danger-btn" onClick={handleClearStorage}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                  </svg>
                  Clear Local Storage
                </button>
              </div>
            </div>

            {/* Troubleshooting Tips */}
            <div className="qts-panel">
              <div className="qts-panel-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                  fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                </svg>
                <span className="qts-panel-title">Troubleshooting Tips</span>
              </div>
              <ol className="qts-tips-list">
                {TIPS?.map((tip, i) => (
                  <li key={i} className="qts-tip-item">
                    <span className="qts-tip-num">{i + 1}</span>
                    <span className="qts-tip-text">{tip}</span>
                  </li>
                ))}
              </ol>

              {/* System Info */}
              <div className="qts-sysinfo">
                <div className="qts-sysinfo-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                  </svg>
                  System Info
                </div>
                <div className="qts-sysinfo-grid">
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">Browser</span>
                    <span className="qts-sysinfo-val">{(() => {
                      const ua = navigator.userAgent;
                      if (ua.includes("Edg/"))    return "Edge";
                      if (ua.includes("OPR/"))    return "Opera";
                      if (ua.includes("Chrome/")) return "Chrome";
                      if (ua.includes("Firefox/"))return "Firefox";
                      if (ua.includes("Safari/")) return "Safari";
                      return "Unknown";
                    })()}</span>
                  </div>
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">Connection</span>
                    <span className="qts-sysinfo-val">{netType ? netType?.toUpperCase() : isOnline ? "Online" : "Offline"}</span>
                  </div>
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">Screen</span>
                    <span className="qts-sysinfo-val">{window?.screen?.width} × {window?.screen?.height}</span>
                  </div>
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">Pixel Ratio</span>
                    <span className="qts-sysinfo-val">{window?.devicePixelRatio}×</span>
                  </div>
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">Language</span>
                    <span className="qts-sysinfo-val">{navigator?.language}</span>
                  </div>
                  <div className="qts-sysinfo-row">
                    <span className="qts-sysinfo-key">CPU Cores</span>
                    <span className="qts-sysinfo-val">{navigator?.hardwareConcurrency ?? "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>{/* /qts-body */}
      </div>
    </Navbar>
  );
};

export default QuickTroubleshoot;
